/**
 * Deckungsbeitrag app v2 — the farmer-facing surface over the
 * ``deckungsbeitrag`` derived facts, rebuilt on the Liquiditätsplanung
 * house shell: LensePanel sidebar (Zeitraum → Kulturen) and the Kulturen feed — the
 * ranked Kultur→Schlag tree table under its title row (what the ranking is per, and the
 * Bezugsgröße switcher that sets it). The ranked bar chart that used to sit above the
 * table, folded by default, was retired on 2026-09-07: the farmer read the table, not the
 * bars, and the switcher moved down with the title. The page opens on the feed card:
 * HOF-20 retired the page-head hero (Σ DB1, its
 * „nicht mitgerechnet"-subline and the Ampel/Verlust banner) because the Σ
 * counted ``ok`` rows alone while the table below showed every Kultur, so on a
 * real corpus it led with a figure standing on 1 of 143 Schläge and the subline
 * existed only to apologise for the gap. No total replaces it — deliberately, and
 * not anywhere else on the page either. ``?period=`` (crop_year) stays the single
 * period source of truth — switching periods is a query-time rollup over
 * the already-fetched facts (design principle 10: period is never an
 * identity). ``?cc=`` deep-links a crop_cycle (stable ``#cc-<id>``
 * anchors, auto-expanded). All data flows through useDeckungsbeitrag; all
 * strings hardcoded German du-form (post-#1772; app pages don't use
 * i18next). (#2354 retired the Kulturen|Betriebszweige feed switcher — the
 * Kulturen feed is the only feed; a stale ``?view=`` param is ignored. #2948 §6
 * retired the chart's Kultur|Schlag switcher the same way — Kultur bars are the
 * only bars and a stale ``?g=`` param is accepted-and-ignored, never rejected:
 * deep links and screenshots carrying it exist and must render, not throw. #2948
 * §4 retired the hide-rows mechanism on the same pattern — every Kultur in the
 * period renders, always, and a stale ``?showIncomplete=`` is inert for EVERY
 * value because nothing reads it.)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { LensePanel, PageLoadOverlay, useMobileSidePanel } from '@hofsync/design-system';
import { useDeckungsbeitrag } from '../../../hooks/useDeckungsbeitrag';
import { usePageLoadTelemetry } from '../../../hooks/usePageLoadTelemetry';
import BasisSwitcher from './BasisSwitcher';
import RankedMarginTable from './RankedMarginTable';
import RowDetailPanel from './RowDetailPanel';
import { periodLensItems } from './periodLensItems';
import { availableBases, BASIS_META, resolveBasis, type MarginBasis } from './basis';
import {
  OHNE_JAHR,
  kulturGroupKey,
  periodsOf,
  rankKulturByBasis,
  rollupByKultur,
  rowsForPeriod,
  withAnalysisUnits,
} from './deckungsbeitragRollup';
import './DeckungsbeitragPage.css';

export default function DeckungsbeitragPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const periodParam = searchParams.get('period');
  const ccParam = searchParams.get('cc');
  // #2442 — the selected cascade line for the source drill-down; meaningful only
  // while a row (?cc=) is open, and cleared whenever ?cc= clears/changes.
  const lineParam = searchParams.get('line');
  const basisParam = searchParams.get('basis');
  const { rows, wjStartMonth, defaultBasis, analysisUnits, fieldNames, isLoading, error } =
    useDeckungsbeitrag();
  const mobilePanel = useMobileSidePanel();

  const periods = useMemo(() => periodsOf(rows), [rows]);
  // Default = the newest non-null period; the null bucket only wins when it
  // is all there is.
  const selectedPeriod = periodParam ?? periods.find((p) => p !== OHNE_JAHR) ?? periods[0] ?? '';

  // HOF-102 — no substitution any more. ``leistung`` on the fact IS Menge x
  // Preisspiegel-Monatspreis, so the rollup, the ranking, the chart, the Je-Einheit panel,
  // the drawer and any export all read the one number the worker computed. HOF-21's
  // ``applyValuationMode`` stood here to swap in a second cascade for a mode the user had
  // chosen; deleting the mode deleted the only way two surfaces on this page could
  // disagree about which € they were showing.
  const periodRows = useMemo(() => rowsForPeriod(rows, selectedPeriod), [rows, selectedPeriod]);
  // ONE Kultur+Sorte rollup feeds the tree table's Level 1 (#2302).
  const kulturRollups = useMemo(() => rollupByKultur(periodRows), [periodRows]);
  // The Bezugsgröße (#2304): "je {basis}" driving DB1/basis for the table and its title.
  // Offered bases are the drawer's five, data-driven (availableBases: € gesamt and ha
  // always, the rest when some row carries the quantity — the single-crop scope gate on
  // Reihenmeter/Pflanze went on 2026-09-07); the active basis resolves ?basis= → 'kg' →
  // tenant default → 'ha' (#2948 §7 — the page lands on the number a farmer prices in).
  //
  // #2948 §4 widened the input from the filtered set to the whole period: a revealed
  // yield-bearing row makes ``kg`` offerable. Pinned in DeckungsbeitragPage.test.tsx.
  const basisOptions = useMemo(() => availableBases(periodRows), [periodRows]);
  const basis = useMemo(
    () => resolveBasis(basisParam, defaultBasis, basisOptions),
    [basisParam, defaultBasis, basisOptions],
  );
  // The feed's title names what the ranking column is per; € gesamt reads as a plain total.
  const feedTitle = basis === 'total' ? 'Deckungsbeitrag gesamt' : `Deckungsbeitrag ${BASIS_META[basis].label}`;
  // Re-rank the (basis-agnostic) rollups for the active basis; for 'ha' this reproduces
  // the #2302 order exactly.
  const rankedGroups = useMemo(() => rankKulturByBasis(kulturRollups, basis), [kulturRollups, basis]);
  // HOF-195 — the Hauptschlag rollup level, applied LAST and to ``children`` only: the
  // Kultur sums and the Level-1 ranking read ``rankedGroups``' own fields
  // and ``members``, so they are arithmetically untouched by it. With no curated unit on
  // the tenant this is the identity, which is why "absent config ⇒ status quo" needs no
  // second code path anywhere on the page.
  const treeGroups = useMemo(
    () => withAnalysisUnits(rankedGroups, analysisUnits, basis),
    [rankedGroups, analysisUnits, basis],
  );

  // The ?cc-addressed leaf row: drives the side panel AND the auto-expand of
  // its parent Kultur (deep links land on the open answer).
  const ccRow = useMemo(
    () => (ccParam ? periodRows.find((row) => row.crop_cycle_id === ccParam) ?? null : null),
    [ccParam, periodRows],
  );
  const selectedGroupKey = ccRow ? kulturGroupKey(ccRow.crop, ccRow.variety) : null;
  // HOF-195 — the analysis unit the ?cc leaf sits in, if any. Resolved by SEARCHING the
  // built tree rather than by re-deriving the key from the row: whether a block is inside
  // a unit depends on the opt-in list AND on the member threshold, and a second derivation
  // of that answer is a second place for it to be wrong. Null when the leaf is a loose
  // Level-2 child, which is every leaf on a tenant with no curated unit.
  const selectedUnitKey = useMemo(() => {
    if (!ccParam) return null;
    for (const group of treeGroups) {
      for (const child of group.children) {
        if (child.kind !== 'unit') continue;
        if (child.unit.members.some((row) => row.crop_cycle_id === ccParam)) return child.key;
      }
    }
    return null;
  }, [ccParam, treeGroups]);

  // Kultur-level expansion is component state, NOT a URL param (#2221
  // precedent: view state that isn't an address). The selected leaf's parent
  // is force-included, so a ?cc deep link always shows its row.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set());
  const effectiveExpanded = useMemo(
    () =>
      selectedGroupKey ? new Set([...expandedGroups, selectedGroupKey]) : expandedGroups,
    [expandedGroups, selectedGroupKey],
  );
  // HOF-195 — the same force-include one level down, so a ``?cc=`` link into a block lands
  // on the open answer at BOTH levels: its Kultur above and its analysis unit between.
  const [expandedUnits, setExpandedUnits] = useState<ReadonlySet<string>>(new Set());
  const effectiveExpandedUnits = useMemo(
    () => (selectedUnitKey ? new Set([...expandedUnits, selectedUnitKey]) : expandedUnits),
    [expandedUnits, selectedUnitKey],
  );
  const lensItems = useMemo(
    () => periodLensItems(rows, wjStartMonth),
    [rows, wjStartMonth],
  );

  const pageLoadOverlay = usePageLoadTelemetry({
    page: 'deckungsbeitrag',
    view: `deckungsbeitrag:period=${selectedPeriod || 'none'}`,
    isLoading,
  });

  // Persist the resolved default into ?period= once (deep-linkable),
  // mirroring liquiditaetsplanung's ?ym= — strictly on the initial load.
  useEffect(() => {
    if (periodParam || isLoading || !selectedPeriod) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('period', selectedPeriod);
        return next;
      },
      { replace: true },
    );
  }, [periodParam, isLoading, selectedPeriod, setSearchParams]);

  // ?cc= deep link: bring the addressed row into view once data is there.
  // (Guarded — jsdom has no scrollIntoView.)
  useEffect(() => {
    if (isLoading || !ccParam) return;
    const el = document.getElementById(`cc-${ccParam}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center' });
    }
  }, [isLoading, ccParam, selectedPeriod]);

  const setParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  // The panel is a flat year filter (no second level): picking a year clears any
  // row selection from the previous year, which a stale ?cc= would otherwise keep
  // addressing. Row selection itself lives on the table (handleSelectCrop).
  const handleLensSelect = useCallback(
    (itemId: string) => {
      // A year switch clears the row selection AND its ?line= drill-down (both
      // address the previous year's Schlag).
      setParams({ period: itemId, cc: null, line: null });
      mobilePanel?.close();
    },
    [setParams, mobilePanel],
  );

  // One selection state for sidebar and table (?cc=): selecting from
  // anywhere unfolds the row; a table re-click toggles it closed. Changing OR
  // clearing the row also clears any ?line= drill-down (#2442) — a line selected
  // on one Schlag is meaningless on the next.
  const handleSelectCrop = useCallback(
    (cropCycleId: string | null) =>
      setParams(cropCycleId ? { cc: cropCycleId, line: null } : { cc: null, line: null }),
    [setParams],
  );

  // #2442 — toggle the selected cascade line (?line=); re-selecting the same line
  // (or passing null) clears it. Deep-linkable like ?cc=.
  const handleSelectLine = useCallback(
    (lineKey: string | null) => setParams({ line: lineKey }),
    [setParams],
  );

  // The basis the page would resolve to with NO ?basis= param — i.e. 'kg' when the
  // data offers it (#2948 §7), else the tenant default, else 'ha'. The clean-URL
  // optimisation keys off THIS, not the literal 'ha': dropping the param on an 'ha'
  // click would otherwise snap straight back to the resolved default (#2319). It calls
  // the SAME resolver with param = null, so the landing basis and the param-drop can
  // never disagree about what "the default" is.
  const resolvedDefaultBasis = useMemo(
    () => resolveBasis(null, defaultBasis, basisOptions),
    [defaultBasis, basisOptions],
  );

  // Bezugsgröße switch → ?basis= (deep-linkable); selecting the resolved tenant
  // default keeps the URL clean (param absent), any other basis is pinned
  // explicitly. One control drives the feed title + the table.
  const handleBasisChange = useCallback(
    (next: MarginBasis) => setParams({ basis: next === resolvedDefaultBasis ? null : next }),
    [setParams, resolvedDefaultBasis],
  );

  // Tree expansion toggle (table caret, group-row click). Single-open (#2345): a
  // toggle opens exactly this Kultur and collapses
  // every other manual expansion — mirrors Liquiditätsplanung's CategoryFeed.
  // Collapsing the selected leaf's parent also clears the selection — otherwise
  // the ?cc-derived force-include would snap it straight open.
  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      // Judge open/closed by the EFFECTIVE state (incl. the ?cc force-include),
      // or collapsing a deep-linked group would re-add it instead of closing.
      const isOpen = effectiveExpanded.has(groupKey);
      if (isOpen && groupKey === selectedGroupKey) {
        handleSelectCrop(null);
      }
      // Single-open: opening replaces the manual-expansion set with just this
      // group; collapsing empties it. The ?cc= force-include still layers a
      // deep-linked selection's parent on top via effectiveExpanded, so a
      // shared link always shows its row even while single-open is in force.
      setExpandedGroups(() => (isOpen ? new Set<string>() : new Set([groupKey])));
    },
    [effectiveExpanded, selectedGroupKey, handleSelectCrop],
  );

  // HOF-195 — the analysis-unit caret. Same two rules as the Kultur toggle, for the same
  // two reasons: judge open/closed by the EFFECTIVE state (or collapsing a deep-linked
  // unit would re-add it), and collapsing the unit that holds the selected block clears
  // the selection (or the ?cc force-include would snap it straight back open).
  //
  // Deliberately NOT single-open. That rule exists at Kultur grain because a Kultur's
  // leaves are a long list; a unit's blocks are the handful a farmer just asked to see
  // together, and closing one Analyseeinheit to open its sibling would fight the very
  // comparison the level was built for.
  const handleToggleUnit = useCallback(
    (unitKey: string) => {
      const isOpen = effectiveExpandedUnits.has(unitKey);
      if (isOpen && unitKey === selectedUnitKey) {
        handleSelectCrop(null);
      }
      setExpandedUnits((prev) => {
        const next = new Set(prev);
        if (isOpen) next.delete(unitKey);
        else next.add(unitKey);
        return next;
      });
    },
    [effectiveExpandedUnits, selectedUnitKey, handleSelectCrop],
  );

  const isEmpty = !isLoading && error == null && rows.length === 0;
  const hasData = !isLoading && error == null && rows.length > 0;

  return (
    <div className={`deckungsbeitrag-page${hasData ? '' : ' deckungsbeitrag-page--no-panel'}`}>
      {hasData && (
        <LensePanel
          title={t('appPanels.deckungsbeitrag', 'Deckungsbeitrag')}
          items={lensItems}
          selectedItem={selectedPeriod}
          onSelect={handleLensSelect}
          isMobileOpen={mobilePanel?.isOpen ?? false}
          onMobileClose={() => mobilePanel?.close()}
        />
      )}
      <PageLoadOverlay
        visible={pageLoadOverlay.showOverlay}
        elapsedSeconds={pageLoadOverlay.elapsedSeconds}
      />
      <div className="deckungsbeitrag-page__main">
        {/* Error wins over the empty state — a failed load otherwise reads
            as "dein Hof hat keine Deckungsbeiträge". */}
        {!isLoading && error != null && (
          <div className="deckungsbeitrag-page__error" role="status">
            Hinweis: Einige Daten konnten nicht geladen werden.
          </div>
        )}

        {isEmpty && (
          <div className="deckungsbeitrag-page__empty">
            <h1 className="deckungsbeitrag-page__title">Deckungsbeitrag</h1>
            <p className="deckungsbeitrag-page__empty-lead">Noch keine Deckungsbeiträge berechnet.</p>
            <p>
              Sobald deine Anbau-Daten verbunden sind — Schläge, Kulturen und Belege — siehst du
              hier, welche Kultur dir wirklich Geld bringt.
            </p>
          </div>
        )}

        {hasData && (
          <>
            {/* The Kulturen feed — the only feed (#2354 retired the Betriebszweige view and
                its Kulturen|Betriebszweige switcher), and since 2026-09-07 the first and only
                card: the ranked bar chart above it was retired. ``periodRows.length === 0`` is
                exactly what HOF-20's ``summary === null`` used to say. */}
            <section className="deckungsbeitrag-page__feed" aria-label="Deckungsbeiträge je Kultur">
              {periodRows.length === 0 ? (
                <p className="deckungsbeitrag-page__none">Keine Einträge in diesem Zeitraum.</p>
              ) : (
                <>
                  {/* The title row: what the ranking column is per, and the switcher that sets
                      it — one control, the title and the table relabel together (#2304). */}
                  <header className="deckungsbeitrag-page__feed-head">
                    <h2 className="deckungsbeitrag-page__feed-title">{feedTitle}</h2>
                    <BasisSwitcher basis={basis} options={basisOptions} onBasisChange={handleBasisChange} />
                  </header>
                  <RankedMarginTable
                  groups={treeGroups}
                  selectedId={ccParam}
                  onSelect={handleSelectCrop}
                  expandedGroups={effectiveExpanded}
                  onToggleGroup={handleToggleGroup}
                  expandedUnits={effectiveExpandedUnits}
                  onToggleUnit={handleToggleUnit}
                    fieldNames={fieldNames}
                    basis={basis}
                  />
                </>
              )}
            </section>
            {/* The Schlag calculation detail — open ⇔ ?cc= set (#2302); #2442 adds
                the per-line source drill-down (?line=), live only with a row open. */}
            <RowDetailPanel
              row={ccRow}
              fieldNames={fieldNames}
              basis={basis}
              selectedLine={ccRow ? lineParam : null}
              onSelectLine={handleSelectLine}
              onNavigate={navigate}
              onClose={() => handleSelectCrop(null)}
            />
          </>
        )}
      </div>
    </div>
  );
}
